<template>
  <Modal ref="modalRef" class="person-set-modal" :ok-disabled="!canSaveChangesRef" @ok="saveChanges">
    <template #title>
      Define matching
    </template>
    <div v-if="hasPlaceholderRef" class="alert alert-light alert-sm">
      This source defines a relationship to the selected profiles.
    </div>
    <div class="instructions">
      Select the profiles this {{ sourceTypeRef }} considers to be matches.
    </div>
    <ul class="list-unstyled profile-list">
      <li v-for="id in profileIdsRef" :key="id.value">
        <div class="form-check">
          <input type="checkbox" class="form-check-input"
            :checked="selectedIdsRef.includes(id.value)"
            :disabled="!canRemove(id)"
            @change="toggleSelected(id)"/>
        </div>
        <ProfileCard
          :personId="id.value"
          small
          show-profile-type
          :this-profile="id.value == personId"
          :select-mode="canRemove(id)"
          :static="!canRemove(id)"
          @click="toggleSelected(id)"/>
      </li>
      <li class="add-row">
        <ProfileSelector v-if="showAddInputRef" ref="selectorRef" :model-value="undefined" @update:model-value="addProfile"/>
        <button v-else type="button" class="btn btn-inline btn-link" @click="showAddInput">
          Add a profile
        </button>
      </li>
    </ul>
  </Modal>
</template>

<style lang="scss" scoped>
:deep(.modal-body) {
  display: flex;
  flex-direction: column;
  gap: 1rem;
}

.instructions {
  font-size: 0.875rem;
  color: #888;
}

.alert {
  margin: 0;
}

.profile-list {
  margin: 0;
  margin-left: 1rem;
  display: flex;
  flex-direction: column;
  gap: 0.5rem;

  li {
    display: grid;
    grid-template-columns: 1rem 1fr;
    column-gap: 1rem;
    align-items: center;

    &.add-row {
      min-height: 40px;

      .profile-selector {
        grid-column: 2;
      }

      button {
        grid-column: 1 / span 2;
        margin-top: 0.5rem;
        font-size: 0.875rem;
      }
    }
  }
}

.profile-selector {
  max-width: 400px;
}
</style>

<script lang="ts" setup>
import { computed, nextTick, ref } from 'vue'
import { PersonSet } from '@/rd/ResearchDataModel'
import { useDataGroupStore } from '@/gp/DataGroupStore'
import { ProfileUtils } from './profiles/ProfileUtils'
import { CompositeId } from '@/rd/CompositeId'
import { useViewPersonStore } from '@/rd/ViewPersonStore'
import ProfileCard from './ProfileCard.vue'
import Modal from '@/util/Modal.vue'
import ProfileSelector from './profiles/ProfileSelector.vue'

const props = defineProps<{
  personId?: string,
}>()

defineExpose({
  open,
})

const personSetRef = ref<PersonSet>()
const ownerIdRef = ref<string>()
const addedIdsRef = ref<string[]>([])
const selectedIdsRef = ref<string[]>([])
const showAddInputRef = ref(false)

const modalRef = ref<InstanceType<typeof Modal>>()
const selectorRef = ref<InstanceType<typeof ProfileSelector>>()

const viewPersonStore = useViewPersonStore()
const dataGroupStore = useDataGroupStore()

// all data should be loaded by parent
const viewPersonRef = computed(() => viewPersonStore.getLoadedPerson(props.personId))
const ownerGroupRef = computed(() => dataGroupStore.getLoadedGroup(ownerGroupIdRef.value))

const isMatchOwnerRef = computed(() => CompositeId.isCompositeId(ownerIdRef.value))
const ownerGroupIdRef = computed(() => isMatchOwnerRef.value ? CompositeId.getGroupId(ownerIdRef.value!) : ownerIdRef.value)
const allIdsRef = computed(() => [...(viewPersonRef.value?.matchIds ?? []), ...addedIdsRef.value])
const profileIdsRef = computed(() => {
  const dataGroups = dataGroupStore.getLoadedGroupList(allIdsRef.value.map(id => CompositeId.getGroupId(id)))
  return ProfileUtils.getNonPlaceholderIds(allIdsRef.value, dataGroups)
    .map(id => CompositeId.parse(id))
})

const sourceTypeRef = computed(() => ownerGroupRef.value?.isProfile ? 'profile' : 'tree')
const hasPlaceholderRef = computed(() => ownerGroupIdRef.value && personSetRef.value?.memberIds.some(id => 
  CompositeId.hasGroupId(id, ownerGroupIdRef.value!) && ProfileUtils.isPlaceholder(id, ownerGroupRef.value!)))

const hasChangesRef = computed(() => !personSetRef.value ||
  selectedIdsRef.value.length != personSetRef.value.memberIds.length ||
  selectedIdsRef.value.some(id => !personSetRef.value!.memberIds.includes(id)))
const isValidSetRef = computed(() => !hasPlaceholderRef.value || selectedIdsRef.value.length > 1)
const canSaveChangesRef = computed(() => hasChangesRef.value && isValidSetRef.value)

function open(set: PersonSet | undefined, ownerId?: string) {
  if (!set && !ownerId)
    throw new Error('PersonSetModal: must provide either a set or owner')

  ownerIdRef.value = ownerId

  if (set) {
    if (ownerId) {
      if (set.groupId != ownerGroupIdRef.value)
        throw new Error(`PersonSetModal: owner ${ownerId} does not match set ${set.id}`)
    }
    else {
      ownerIdRef.value = set.groupId // implied
    }
  }
  else {
    // NOTE: it's possible for the specified owner to have multiple existing sets that include one or more matches
    // for the specified person, which if no set is specified, would create ambiguity about which set we're trying 
    // to modify. However, any set that is not a singleton should be displayed by the parent and passed to this modal
    // as the one being modified. So if no set is specified, then all existing sets must be singletons. And in that 
    // case, it doesn't matter which set we modify, because none of them currently have any effect.
    set = viewPersonStore.getLoadedPersonSets(props.personId)
      .find(s => s.groupId == ownerGroupIdRef.value) // choose first one with specified owner
  }

  personSetRef.value = set
  addedIdsRef.value = []
  selectedIdsRef.value = [...(set?.memberIds ?? [])] // make a copy!
  showAddInputRef.value = false

  modalRef.value?.open()
}

function canRemove(id: CompositeId) {
  return !(isMatchOwnerRef.value && id.value == ownerIdRef.value);
}

function toggleSelected(id: CompositeId) {
  const index = selectedIdsRef.value.indexOf(id.value)
  if (index >= 0) {
    selectedIdsRef.value.splice(index, 1)
  } else {
    selectedIdsRef.value.push(id.value)
  }
}

async function showAddInput() {
  showAddInputRef.value = true
  await nextTick()
  selectorRef.value?.focus()
}

function addProfile(id: string) {
  if (id && !profileIdsRef.value.find(pid => pid.value == id)) {
    addedIdsRef.value.push(id)
  }  
  showAddInputRef.value = false
}

async function saveChanges() {
  if (personSetRef.value) {
    const newMemberIds = selectedIdsRef.value
    const removedMemberIds = personSetRef.value.memberIds.filter(id => !newMemberIds.includes(id))
    await viewPersonStore.definePersonSetsAsync([newMemberIds, ...removedMemberIds.map(id => [id])], ownerGroupIdRef.value!)
  }
  else {
    // new set
  }
  modalRef.value?.close()
}
</script>
